/**
 * 部门树级联选择统一 props / 数据清洗。
 *
 * 背景：子部门账号的学校/校区/年级等祖先会带 disabled（仅作路径引导）。
 * Element Cascader 在 checkStrictly=false 时禁止展开 disabled 节点；
 * 空数组 children: [] 还会多出一列「暂无数据」。
 *
 * 全站约定（与历史逻辑一致）：
 * - 默认只能选「叶子」（班级等），上级只负责展开
 * - 选上级部门等场景用 getDeptCascaderProps({ checkStrictly: true })
 * - options 先走 prepareDeptCascaderTree
 * - 顶部已固定学校时，树请求必须带 scopeDepartmentId，勿加载全平台
 *
 * 创建于 2026-08-26
 */

/**
 * 部门/学校 ID 保持十进制字符串。雪花 ID 超过 Number 安全整数时不能用 Number()。最后更新：2026-10-15
 * @param {*} value
 * @returns {string} 无效时返回空字符串
 */
export function normalizeDeptId(value) {
  if (value === undefined || value === null) return ''
  const str = String(value).trim()
  if (!/^\d+$/.test(str) || str === '0') return ''
  return str
}

/**
 * 树节点 / 接口字段取部门 Id（与 normalizeDeptId 相同，语义更直观）。最后修改：2026-10-18
 * @param {*} value
 * @returns {string}
 */
export function toDeptId(value) {
  return normalizeDeptId(value)
}

/**
 * 从树节点 data 取 Id 字符串。最后修改：2026-10-18
 * @param {object} data
 * @returns {string}
 */
export function treeNodeDeptId(data) {
  if (!data) return ''
  if (data.value != null && data.value !== '') return normalizeDeptId(data.value)
  if (data.Id != null && data.Id !== '') return normalizeDeptId(data.Id)
  if (data.id != null && data.id !== '') return normalizeDeptId(data.id)
  return ''
}

/**
 * 比较两个部门 ID。兼容历史上被 Number() 截断后仍写在本地的雪花 ID。最后更新：2026-10-15
 * @param {*} left
 * @param {*} right
 * @returns {boolean}
 */
export function deptIdsEqual(left, right) {
  const leftId = normalizeDeptId(left)
  const rightId = normalizeDeptId(right)
  if (!leftId || !rightId) return false
  if (leftId === rightId) return true
  return leftId.length > 15 && rightId.length > 15 && Number(leftId) === Number(rightId)
}

/**
 * 读取当前顶部固定学校 ID。最后更新：2026-10-15
 * @param {Vue|object} [vm] - 组件实例（优先读 $store）
 * @returns {string} 无效时返回空字符串
 */
export function resolveActiveSchoolId(vm) {
  const fromStore = vm && vm.$store && vm.$store.state
    ? normalizeDeptId(vm.$store.state.activeSchoolId)
    : ''
  if (fromStore) return fromStore
  try {
    const fromStorage = normalizeDeptId(window.localStorage.getItem('activeSchoolId'))
    if (fromStorage) return fromStorage
  } catch (e) { /* ignore */ }
  return ''
}

/**
 * 将带 scope 的部门树响应转为级联 options（去掉总平台，从学校子树起）。
 * @param {object|null|undefined} responseRoot
 * @param {{ clearAllDisabled?: boolean, keepSchoolRoot?: boolean, internalOnly?: boolean }} [options]
 * @returns {Array}
 */
export function scopedDeptTreeToCascaderOptions(responseRoot, options) {
  const opts = options || {}
  let root = responseRoot
  if (opts.internalOnly) {
    const normalized = normalizeInternalDirectoryTree(root)
    if (Array.isArray(normalized)) {
      root = normalized.length === 1 ? normalized[0] : { children: normalized }
    } else {
      root = normalized
    }
  }
  if (!root) return []
  const type = String(root.DeptOrgType || root.deptOrgType || '').toLowerCase()
  const value = Number(root.value || 0)
  const label = String(root.label || root.Name || '')
  // 虚拟总平台 / 空类型根：直接用其子节点
  if (value === 0 || label === '总平台' || (!type && root.children && root.children.length)) {
    return prepareDeptCascaderTree(root.children || [], opts)
  }
  if (opts.keepSchoolRoot || type === 'school') {
    return prepareDeptCascaderTree([root], opts)
  }
  if (root.children && root.children.length) {
    return prepareDeptCascaderTree(root.children, opts)
  }
  return prepareDeptCascaderTree([root], opts)
}

/**
 * 是否为家校专用节点（校区/学段/年级/班级或 BigType=1）。最后更新：2026-08-30
 * @param {object} node
 * @returns {boolean}
 */
export function isHomeSchoolOnlyDeptNode(node) {
  if (!node || typeof node !== 'object') return false
  const orgType = String(node.DeptOrgType || node.deptOrgType || '').toLowerCase()
  if (orgType === 'school' || orgType === 'affiliateschool' || orgType === 'platform') return false
  if (['campus', 'period', 'grade', 'class'].indexOf(orgType) !== -1) return true
  const bigType = Number(node.BigType != null ? node.BigType : node.bigType)
  return bigType === 1
}

/**
 * 内部通讯录树：剔除校区等家校节点，并将其下行政部门抬升保留。最后更新：2026-08-30
 * @param {object|Array|null|undefined} node
 * @returns {object|Array|null}
 */
export function normalizeInternalDirectoryTree(node) {
  if (!node) return null
  if (Array.isArray(node)) {
    const list = []
    node.forEach((item) => {
      const normalized = normalizeInternalDirectoryTree(item)
      if (Array.isArray(normalized)) list.push(...normalized)
      else if (normalized) list.push(normalized)
    })
    return list
  }

  if (isHomeSchoolOnlyDeptNode(node)) {
    const lifted = []
    ;(node.children || []).forEach((child) => {
      const normalized = normalizeInternalDirectoryTree(child)
      if (Array.isArray(normalized)) lifted.push(...normalized)
      else if (normalized) lifted.push(normalized)
    })
    return lifted
  }

  const copy = Object.assign({}, node)
  const nextChildren = []
  ;(node.children || []).forEach((child) => {
    const normalized = normalizeInternalDirectoryTree(child)
    if (Array.isArray(normalized)) nextChildren.push(...normalized)
    else if (normalized) nextChildren.push(normalized)
  })
  copy.children = nextChildren
  return copy
}

/**
 * 将 normalize 后可能为数组的根节点收敛为单根，避免 el-tree 数据结构异常。最后更新：2026-09-01
 * @param {object|Array|null|undefined} tree
 * @returns {object|null}
 */
export function coalesceDirectoryTreeRoot(tree) {
  if (!tree) return null
  if (Array.isArray(tree)) {
    if (tree.length === 1) return tree[0]
    if (!tree.length) return null
    return {
      label: '总平台',
      value: 0,
      DeptOrgType: 'platform',
      children: tree,
    }
  }
  return tree
}

/**
 * 内部通讯录级联 options：先剔家校节点再清洗。最后更新：2026-08-30
 * @param {Array|object|null|undefined} nodes
 * @param {{ clearAllDisabled?: boolean }} [options]
 * @returns {Array}
 */
export function prepareInternalDeptCascaderTree(nodes, options) {
  const normalized = normalizeInternalDirectoryTree(nodes)
  const list = Array.isArray(normalized) ? normalized : (normalized ? [normalized] : [])
  return prepareDeptCascaderTree(list, options)
}


/** 默认：只能选叶子（班级），与改权限前一致 */
export const DEPT_CASCADER_PROPS = {
  value: 'value',
  label: 'label',
  children: 'children',
  disabled: 'disabled',
  expandTrigger: 'hover',
  checkStrictly: false,
}

/**
 * 可选任意未禁用节点（如部门管理「上级部门」可选年级）。
 */
export const DEPT_CASCADER_PROPS_ANY_NODE = Object.assign({}, DEPT_CASCADER_PROPS, {
  checkStrictly: true,
})

/**
 * @param {Record<string, any>} [overrides]
 * @returns {Record<string, any>}
 */
export function getDeptCascaderProps(overrides) {
  return Object.assign({}, DEPT_CASCADER_PROPS, overrides || {})
}

/**
 * 清洗部门树供级联使用。
 * - 有子节点的上级去掉 disabled，保证可展开到有权限的班级
 * - 去掉空 children，避免末级「暂无数据」
 * - 叶子保留原 disabled（无权限的班级不可选）
 * @param {Array|object|null|undefined} nodes
 * @param {{ clearAllDisabled?: boolean }} [options]
 * @returns {Array}
 */
export function prepareDeptCascaderTree(nodes, options) {
  const clearAllDisabled = !!(options && options.clearAllDisabled)
  const list = Array.isArray(nodes) ? nodes : (nodes ? [nodes] : [])

  const walk = (node) => {
    if (!node || typeof node !== 'object') return null
    const rawChildren = Array.isArray(node.children) ? node.children : []
    const children = rawChildren.map(walk).filter(Boolean)
    const next = Object.assign({}, node)

    if (children.length > 0) {
      next.children = children
      // 有下级的引导节点必须可展开
      next.disabled = false
    } else {
      if (Object.prototype.hasOwnProperty.call(next, 'children')) {
        delete next.children
      }
      next.disabled = clearAllDisabled ? false : !!node.disabled
    }
    return next
  }

  return list.map(walk).filter(Boolean)
}

/**
 * 判断是否为班级（末级）节点。最后更新：2026-08-31
 * @param {object|null|undefined} data
 * @param {object|null|undefined} node - el-tree 节点
 * @returns {boolean}
 */
export function isClassDeptNode(data, node) {
  if (!data) return false
  const orgType = String(data.DeptOrgType || data.deptOrgType || '').toLowerCase()
  if (orgType === 'class') return true
  const childNodes = node && node.childNodes
  const isLeaf = !childNodes || childNodes.length === 0
  if (!isLeaf) return false
  return ['school', 'affiliateschool', 'campus', 'period', 'grade'].indexOf(orgType) === -1
}

/**
 * 从左侧机构树节点还原级联路径。最后更新：2026-08-31
 * @param {object|null|undefined} node - el-tree 节点
 * @returns {number[]}
 */
export function buildDeptPathFromTreeNode(node) {
  if (!node) return []
  const path = []
  let cur = node
  while (cur && cur.data && cur.data.value !== undefined && cur.data.value !== null) {
    const val = Number(cur.data.value)
    if (Number.isFinite(val) && val > 0) {
      path.unshift(val)
    }
    if (!cur.parent || cur.parent.level === 0) break
    cur = cur.parent
  }
  return path
}

/**
 * 规范化级联路径为 number 数组。最后更新：2026-08-31
 * @param {Array} path
 * @returns {number[]}
 */
export function normalizeCascaderPath(path) {
  if (!Array.isArray(path) || !path.length) return []
  return path.map(id => Number(id)).filter(id => Number.isFinite(id) && id > 0)
}

/**
 * 深拷贝机构树节点供级联使用。最后更新：2026-08-31
 * @param {object|null|undefined} node
 * @returns {object|null}
 */
export function cloneDeptCascaderNode(node) {
  const prepared = prepareDeptCascaderTree(node ? [node] : [])
  return prepared.length ? prepared[0] : null
}

/**
 * 子节点是否均为班级（可直接平铺展示）。最后更新：2026-08-31
 * @param {object|null|undefined} node
 * @returns {boolean}
 */
export function hasOnlyClassChildren(node) {
  const children = node && node.children
  if (!children || !children.length) return false
  return children.every((child) => {
    const orgType = String(child.DeptOrgType || child.deptOrgType || '').toLowerCase()
    if (orgType === 'class') return true
    const sub = child.children
    return !sub || !sub.length
  })
}

/**
 * 在部门树中递归查找指定部门的路径。最后更新：2026-08-31
 * @param {object|null|undefined} node
 * @param {number|string} deptId
 * @param {Array} path
 * @returns {number[]|null}
 */
export function findPathByDeptId(node, deptId, path) {
  if (!node) return null
  const currentPath = [...path, node.value]
  if (String(node.value) === String(deptId)) {
    return currentPath
  }
  if (node.children && node.children.length) {
    for (let i = 0; i < node.children.length; i++) {
      const found = findPathByDeptId(node.children[i], deptId, currentPath)
      if (found) return found
    }
  }
  return null
}

/**
 * 在已加载的级联 options 内解析默认选中路径（相对 options 根，非整棵组织树）。最后更新：2026-08-31
 * @param {Array} options
 * @param {number|string} deptId
 * @returns {Array}
 */
export function resolveDefaultPathInOptions(options, deptId) {
  if (deptId == null || deptId === '' || !options || !options.length) return []
  for (let i = 0; i < options.length; i++) {
    const path = findPathByDeptId(options[i], deptId, [])
    if (path && path.length) return path
  }
  return []
}

/**
 * 将路径各段与 options 中 value 类型对齐（避免 number/string 不一致导致不回显）。最后更新：2026-08-31
 * @param {Array} options
 * @param {Array} path
 * @returns {Array}
 */
export function coercePathToOptions(options, path) {
  if (!Array.isArray(path) || !path.length || !options || !options.length) return []
  const result = []
  let nodes = options
  for (let i = 0; i < path.length; i++) {
    const segment = path[i]
    const found = nodes.find(n => String(n.value) === String(segment))
    if (!found) return []
    result.push(found.value)
    nodes = found.children || []
  }
  return result
}

/**
 * 清洗级联 id 数组，保留原始类型（勿 Number 大整数）。最后更新：2026-08-31
 * @param {Array} ids
 * @returns {Array}
 */
export function sanitizeDeptCascaderIds(ids) {
  if (!Array.isArray(ids)) return []
  return ids.filter(id => id != null && id !== '' && String(id) !== '0')
}

/**
 * 按学校加载部门级联树，并可选回显指定班级路径。最后更新：2026-08-31
 * @param {{ schoolId: number, preselectDeptId?: number|string, fetchTreeByPid: Function }} params
 * @returns {Promise<{ options: Array, defaultPath: number[] }>}
 */
export async function buildDeptCascaderBySchool({ schoolId, preselectDeptId, fetchTreeByPid }) {
  if (!schoolId) return { options: [], defaultPath: [] }
  const treeRoot = await fetchTreeByPid(schoolId)
  const options = prepareDeptCascaderTree(treeRoot ? [treeRoot] : [])
  const defaultPath = preselectDeptId
    ? resolveDefaultPathInOptions(options, preselectDeptId)
    : []
  return { options, defaultPath }
}

/**
 * 以左侧当前选中节点为起点加载级联树。最后更新：2026-08-31
 * @param {{ selectedData: object, fetchTreeByPid: Function, fallbackDeptId?: number|string, getSchoolId?: Function }} params
 * @returns {Promise<{ options: Array, defaultPath: number[] }>}
 */
export async function buildDeptCascaderFromSelectedNode({
  selectedData,
  fetchTreeByPid,
  fallbackDeptId,
  getSchoolId,
}) {
  if (!selectedData) {
    const schoolId = getSchoolId && getSchoolId()
    if (schoolId) {
      return buildDeptCascaderBySchool({
        schoolId,
        preselectDeptId: fallbackDeptId,
        fetchTreeByPid,
      })
    }
    return { options: [], defaultPath: [] }
  }

  if (selectedData.children && selectedData.children.length > 0) {
    if (hasOnlyClassChildren(selectedData)) {
      return {
        options: selectedData.children.map(c => cloneDeptCascaderNode(c)).filter(Boolean),
        defaultPath: [],
      }
    }
    const cloned = cloneDeptCascaderNode(selectedData)
    return { options: cloned ? [cloned] : [], defaultPath: [] }
  }

  const deptId = normalizeDeptId(selectedData.value)
  if (!deptId) {
    const schoolId = getSchoolId && getSchoolId()
    if (schoolId) {
      return buildDeptCascaderBySchool({
        schoolId,
        preselectDeptId: fallbackDeptId,
        fetchTreeByPid,
      })
    }
    return { options: [], defaultPath: [] }
  }

  const apiRoot = await fetchTreeByPid(deptId)
  if (apiRoot) {
    apiRoot.label = selectedData.label || apiRoot.label
    apiRoot.value = apiRoot.value || deptId
    const children = apiRoot.children || []
    if (children.length && children.every(c => String(c.DeptOrgType || c.deptOrgType || '').toLowerCase() === 'class')) {
      return {
        options: children.map(c => cloneDeptCascaderNode(c)).filter(Boolean),
        defaultPath: [],
      }
    }
    return { options: prepareDeptCascaderTree([apiRoot]), defaultPath: [] }
  }
  const cloned = cloneDeptCascaderNode(selectedData)
  return { options: cloned ? [cloned] : [], defaultPath: [] }
}

/**
 * 新增时已选班级：预填当前班，并从父级节点加载同级班级供切换。最后更新：2026-08-31
 * @param {{ selectedNode: object, fetchTreeByPid: Function, loadFromSelected: Function }} params
 * @returns {Promise<{ options: Array, defaultPath: number[] }>}
 */
export async function buildDeptCascaderForClassSwitch({ selectedNode, fetchTreeByPid, loadFromSelected }) {
  const defaultPath = normalizeCascaderPath(buildDeptPathFromTreeNode(selectedNode))
  const parentNode = selectedNode && selectedNode.parent
  const parentData = parentNode && parentNode.data
  if (parentData) {
    if (parentData.children && parentData.children.length) {
      const cloned = cloneDeptCascaderNode(parentData)
      return { options: cloned ? [cloned] : [], defaultPath }
    }
    const parentId = normalizeDeptId(parentData.value)
    if (parentId) {
      const apiRoot = await fetchTreeByPid(parentId)
      if (apiRoot) {
        apiRoot.label = parentData.label || apiRoot.label
        apiRoot.value = apiRoot.value || parentId
        return { options: prepareDeptCascaderTree([apiRoot]), defaultPath }
      }
    }
  }
  const result = await loadFromSelected()
  return { options: result.options, defaultPath: defaultPath.length ? defaultPath : result.defaultPath }
}

/**
 * 初始化弹窗所属机构级联（与 DeptCourse 一致）。最后更新：2026-08-31
 * @param {{ formType: 'add'|'edit', selectedData: object, selectedNode: object, preselectDeptId?: number|string, departmentId?: number|string, getSchoolId: Function, fetchTreeByPid: Function }} params
 * @returns {Promise<{ options: Array, defaultPath: number[] }>}
 */
export async function initDeptCascaderFormOptions({
  formType,
  selectedData,
  selectedNode,
  preselectDeptId,
  departmentId,
  existingDids,
  getSchoolId,
  fetchTreeByPid,
}) {
  const isAdd = formType === 'add'
  const deptId = preselectDeptId || departmentId

  const loadFromSelected = () => buildDeptCascaderFromSelectedNode({
    selectedData,
    fetchTreeByPid,
    fallbackDeptId: deptId,
    getSchoolId,
  })

  let result
  if (isAdd && isClassDeptNode(selectedData, selectedNode)) {
    result = await buildDeptCascaderForClassSwitch({
      selectedNode,
      fetchTreeByPid,
      loadFromSelected,
    })
  } else if (!isAdd && deptId) {
    result = await buildDeptCascaderBySchool({
      schoolId: typeof getSchoolId === 'function' ? getSchoolId() : 0,
      preselectDeptId: deptId,
      fetchTreeByPid,
    })
  } else if (isAdd && selectedData) {
    result = await loadFromSelected()
  } else {
    result = await buildDeptCascaderBySchool({
      schoolId: typeof getSchoolId === 'function' ? getSchoolId() : 0,
      preselectDeptId: deptId,
      fetchTreeByPid,
    })
  }

  // 新增且左侧已选班级（末级）：在 options 子树内回填
  if (isAdd && isClassDeptNode(selectedData, selectedNode) && selectedData && selectedData.value != null) {
    const pathInOptions = resolveDefaultPathInOptions(result.options, selectedData.value)
    if (pathInOptions.length) {
      result.defaultPath = pathInOptions
    }
  }

  // 编辑：按 DepartmentId / 已有 Dids 在 options 内回显
  if (!isAdd && result.options && result.options.length) {
    let pathInOptions = resolveDefaultPathInOptions(result.options, deptId)
    if (!pathInOptions.length && existingDids && existingDids.length) {
      pathInOptions = coercePathToOptions(result.options, sanitizeDeptCascaderIds(existingDids))
    }
    if (!pathInOptions.length && existingDids && existingDids.length) {
      const lastId = existingDids[existingDids.length - 1]
      pathInOptions = resolveDefaultPathInOptions(result.options, lastId)
    }
    if (pathInOptions.length) {
      result.defaultPath = pathInOptions
    }
  }

  return result
}
